import { useCallback, useEffect, useRef, useState } from 'react'

import { useApiClient } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import { useAgeGateStore } from '../../ageGate/stores/ageGateStore'

interface LegalReconsentResponse {
  success?: boolean
  data?: {
    legalReconsentRequired?: boolean
    currentLegalVersion?: string | null
  }
}

/**
 * Session-scoped so the prompt is asked once, not on every screen that mounts the gate.
 * Reset only by a full reload, which is the behaviour we want: decline signs the user out.
 */
let hasPromptedThisSession = false

export function resetLegalReconsentSessionStateForTests(): void {
  hasPromptedThisSession = false
}

export interface UseLegalReconsentResult {
  isPromptVisible: boolean
  isSubmitting: boolean
  hasError: boolean
  accept: () => Promise<void>
  decline: () => Promise<void>
}

/**
 * Asks a signed-in user to re-accept when the published legal version has moved on.
 *
 * Guests are excluded (the server never reports them as requiring re-consent), and a
 * failed status request is treated as "do not prompt" — a backend hiccup must not lock
 * anyone out of the app.
 */
export function useLegalReconsent(): UseLegalReconsentResult {
  const apiClient = useApiClient()
  const apiClientRef = useRef(apiClient)
  const { isLoaded, isSignedIn, isAnonymous, signOut } = useSupabaseAuth()
  // Waits for the age gate: its prompt comes first, and two native modals stacked on iOS stop
  // taking touches. Anyone not verified 18+ is either being prompted or blocked anyway.
  const ageStatus = useAgeGateStore((state) => state.status)
  const [isPromptVisible, setIsPromptVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    apiClientRef.current = apiClient
  }, [apiClient])

  useEffect(() => {
    if (!isLoaded || !isSignedIn || isAnonymous) return
    if (ageStatus !== 'verified') return
    if (hasPromptedThisSession) return
    const client = apiClientRef.current
    if (!client) return

    let cancelled = false
    client<LegalReconsentResponse>(API_ENDPOINTS.users.privacyPreferences)
      .then((response) => {
        if (cancelled) return
        if (response.data?.data?.legalReconsentRequired === true) {
          hasPromptedThisSession = true
          setIsPromptVisible(true)
        }
      })
      .catch(() => {
        // Never block the app on a status failure; try again next session.
      })
    return () => {
      cancelled = true
    }
  }, [isLoaded, isSignedIn, isAnonymous, ageStatus])

  const accept = useCallback(async () => {
    const client = apiClientRef.current
    if (!client) return
    setIsSubmitting(true)
    setHasError(false)
    try {
      await client(API_ENDPOINTS.users.privacyPreferences, {
        method: 'PATCH',
        data: { legalTermsAccepted: true },
      })
      setIsPromptVisible(false)
    } catch {
      setHasError(true)
    } finally {
      setIsSubmitting(false)
    }
  }, [])

  const decline = useCallback(async () => {
    setIsPromptVisible(false)
    await signOut()
  }, [signOut])

  return { isPromptVisible, isSubmitting, hasError, accept, decline }
}
